/**
 * QuickRecoverySkillEffect — Specter Phantom/Puppeteer terminal passive.
 *
 * Stamina recharges at 2x rate (both walking and stationary).
 * The surgical operator cycles fast — burn resources, recover, burn again.
 *
 * On equip: sets `rechargeMultiplier` on StaminaComponent to 2.0.
 * On unequip: resets `rechargeMultiplier` to 1.0 (default).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-080 (terminal passive pattern)
 * @see specter-drift-path-proposal.md § Quick Recovery
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface QuickRecoveryDeps {
  readonly stamina: {
    /** Multiplier applied to stamina recharge rate (default 1.0). */
    rechargeMultiplier: number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface QuickRecoveryConfig {
  /** Recharge rate multiplier to apply (2.0 = double recharge). */
  readonly rechargeMultiplier: number;
}

const DEFAULT_CONFIG: QuickRecoveryConfig = {
  rechargeMultiplier: 2.0,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class QuickRecoverySkillEffect implements SkillEffect {
  readonly skillId = 'quick_recovery';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;
  readonly staminaCost: number = 0;

  private readonly deps: QuickRecoveryDeps;
  private readonly config: QuickRecoveryConfig;

  /** Whether the multiplier has been applied (guards against double-application). */
  private _applied = false;

  constructor(deps: QuickRecoveryDeps, config: Partial<QuickRecoveryConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  deactivate(): void {}
  isActive(): boolean { return false; }

  update(_delta: number): void {
    if (!this._applied) {
      this.deps.stamina.rechargeMultiplier = this.config.rechargeMultiplier;
      this._applied = true;
    }
  }

  destroy(): void {
    if (this._applied) {
      this.deps.stamina.rechargeMultiplier = 1.0;
      this._applied = false;
    }
  }
}
